import { useState } from 'react';
import { m } from 'framer-motion';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';

// components
import { MotionViewport, varFade } from 'src/components/animate';
import Iconify from 'src/components/iconify';

// utils
import { testimonials } from 'src/utils/data';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  textAlign: 'center',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: theme.customShadows.z8,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.customShadows.z16,
  },
}));

const StyledQuoteIcon = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
  color: 'white',
}));

// ----------------------------------------------------------------------

export default function HomeTestimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating) =>
    Array.from({ length: rating }, (_, index) => (
      <Iconify
        key={index}
        icon="mdi:star"
        sx={{ color: theme.palette.warning.main, width: 20, height: 20 }}
      />
    ));

  return (
    <Box
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 2, md: 4 },
        bgcolor: 'background.neutral',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '100vh' },
      }}
    >
      <Container maxWidth="xl" component={MotionViewport} sx={{ width: '100%' }}>
        <Stack spacing={2} sx={{ textAlign: 'center', mb: { xs: 3, md: 5 }, width: '100%' }}>
          <m.div variants={varFade().inDown}>
            <Typography variant="overline" sx={{ color: 'text.disabled', textAlign: 'center' }}>
              What Our Customers Say
            </Typography>
          </m.div>

          <m.div variants={varFade().inDown}>
            <Typography
              variant="h2"
              sx={{
                my: 2,
                textAlign: 'center',
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                px: { xs: 1, sm: 0 },
              }}
            >
              Trusted by Industry
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Leaders
              </Box>
            </Typography>
          </m.div>

          <m.div variants={varFade().inDown}>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                textAlign: 'center',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Don&apos;t just take our word for it. Here&apos;s what restaurant owners and managers
              are saying about their experience with Cartify.
            </Typography>
          </m.div>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            maxWidth: 800,
            mx: 'auto',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <m.div variants={varFade().inUp} style={{ width: '100%', maxWidth: '800px' }}>
            <StyledCard>
              <StyledQuoteIcon>
                <Iconify icon="mdi:format-quote-open" width={25} height={25} />
              </StyledQuoteIcon>

              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, fontStyle: 'italic', fontSize: '1rem', textAlign: 'center' }}
                >
                  &quot;{testimonials[currentIndex].content}&quot;
                </Typography>

                <Stack spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                  <Avatar
                    src={testimonials[currentIndex].avatar}
                    alt={testimonials[currentIndex].name}
                    sx={{ width: 56, height: 56, mb: 0.5 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    {testimonials[currentIndex].name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    {testimonials[currentIndex].role} at {testimonials[currentIndex].company}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, justifyContent: 'center' }}>
                    {renderStars(testimonials[currentIndex].rating)}
                  </Stack>
                </Stack>
              </CardContent>
            </StyledCard>
          </m.div>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'center',
            mt: 3,
            width: '100%',
          }}
        >
          <IconButton
            onClick={prevTestimonial}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <Iconify icon="mdi:chevron-left" />
          </IconButton>
          <IconButton
            onClick={nextTestimonial}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <Iconify icon="mdi:chevron-right" />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'center',
            mt: 2,
            width: '100%',
          }}
        >
          {testimonials.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'primary.main' : 'divider',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
